// src/components/gestionEvento/Ajustes/Competidores/DashboardInscritos.js
import React, { useState, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Tabs, Tab, Paper,
  useTheme, useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useCompetencia } from '../../../../context/CompetenciaContext';

import DashboardTab from './DashboardTab';
import ListsTab from './ListsTab';

const DashboardInscritos = ({ competencia: competenciaProp }) => {
  const { slug, competitionId } = useParams();
  const { competencias } = useCompetencia();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);

  /* ----- Competencia actual ----- */
  const competition = useMemo(() => {
    if (competenciaProp) return competenciaProp;
    return (
      competencias.find((c) => c.id === competitionId) ||
      competencias.find((c) => c.slug === slug)
    );
  }, [competencias, competenciaProp, slug, competitionId]);

  /* ----- Series: inscritos diarios (total, varonil, femenil) ----- */
  const inscritosDiarios = useMemo(() => {
    const daily = {};
    const list = competition?.inscripciones || [];
    list.forEach((insc) => {
      const fecha = new Date(insc.fechaRegistro).toLocaleDateString();
      if (!daily[fecha]) daily[fecha] = { fecha, total: 0, varonil: 0, femenil: 0 };
      daily[fecha].total += 1;

      const rama = (insc.rama || insc.genero || insc.gender || '').toString().toLowerCase();
      if (['varonil', 'masculino', 'male', 'm', 'hombre'].includes(rama)) daily[fecha].varonil += 1;
      else if (['femenil', 'femenino', 'female', 'f', 'mujer'].includes(rama)) daily[fecha].femenil += 1;
    });
    return Object.values(daily).sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  }, [competition]);

  /* ----- Conteo por género ----- */
  const genero = useMemo(() => {
    let male = 0, female = 0, other = 0;
    (competition?.inscripciones || []).forEach((i) => {
      const raw = (i.rama || i.genero || i.gender || '').toString().toLowerCase();
      if (['varonil', 'masculino', 'male', 'm', 'hombre'].includes(raw)) male += 1;
      else if (['femenil', 'femenino', 'female', 'f', 'mujer'].includes(raw)) female += 1;
      else other += 1;
    });
    return { male, female, other, total: male + female + other };
  }, [competition]);

  /* ----- Barras por categoría (apiladas por género) ----- */
  const categoriaBars = useMemo(() => {
    const map = new Map();
    (competition?.inscripciones || []).forEach((i) => {
      const catObj = i.categoria || i.category || {};
      const cat =
        i.categoriaNombre ||
        i.categoryName ||
        (typeof catObj === 'string' ? catObj : catObj.nombre || catObj.name) ||
        'Sin categoría';

      const raw = (i.rama || i.genero || i.gender || '').toString().toLowerCase();
      const isM = ['varonil', 'masculino', 'male', 'm', 'hombre'].includes(raw);
      const isF = ['femenil', 'femenino', 'female', 'f', 'mujer'].includes(raw);

      const curr = map.get(cat) || { categoria: cat, male: 0, female: 0, total: 0 };
      curr.total += 1;
      if (isM) curr.male += 1;
      if (isF) curr.female += 1;
      map.set(cat, curr);
    });
    return Array.from(map.values()).sort((a, b) => a.categoria.localeCompare(b.categoria));
  }, [competition]);

  /* ----- KPIs ----- */
  const inscritos = competition?.inscripciones ? competition.inscripciones.length : 0;
  const totalIngresos = inscritos * (Number(competition?.general?.precioInscripciones) || 0);

  let equipos = [];
  if (competition?.equipos?.length) {
    equipos = competition.equipos;
  } else if (competition?.inscripciones?.length) {
    const map = new Map();
    competition.inscripciones.forEach((insc) => {
      if (!insc.equipo) return;
      if (typeof insc.equipo === 'object') {
        const key = insc.equipo.id || insc.equipo.name;
        map.set(key, insc.equipo);
      } else {
        map.set(insc.equipo, { id: '', name: insc.equipo, logoEquipo: '', teamCode: '' });
      }
    });
    equipos = Array.from(map.values());
  }

  // Render
  if (!competition) {
    return (
      <Box sx={{ display: 'flex', height: '80vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header - Fuera del Paper como en Categorías y Pruebas */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2, mb: 0.5 }}>
          Competidores
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gestiona inscritos, relevos y métricas generales de tu competencia.
        </Typography>
      </Box>

      {/* Tabs - Fuera del Paper como en Categorías y Pruebas */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          pb: 1.5,
          pt: 0.5,
          background: `linear-gradient(180deg, ${theme.palette.background.default} 70%, ${alpha(
            theme.palette.background.default, 0
          )} 100%)`,
          mb: 2
        }}
      >
        <Paper
          elevation={0}
          sx={{
            px: { xs: 0.75, sm: 1 },
            py: 0.5,
            borderRadius: 999,
            display: 'inline-flex',
            border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(6px)',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            TabIndicatorProps={{
              sx: {
                height: '100%',
                borderRadius: 999,
                zIndex: -1,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
            }}
            sx={{ minHeight: 44, '& .MuiTabs-flexContainer': { gap: 0.5 } }}
          >
            <Tab
              label="Dashboard"
              sx={{
                minHeight: 40, textTransform: 'none', fontWeight: 700,
                px: { xs: 1.5, sm: 2.25 }, borderRadius: 999,
                color: 'text.secondary', '&.Mui-selected': { color: theme.palette.primary.main },
              }}
            />
            <Tab
              label="Listas"
              sx={{
                minHeight: 40, textTransform: 'none', fontWeight: 700,
                px: { xs: 1.5, sm: 2.25 }, borderRadius: 999,
                color: 'text.secondary', '&.Mui-selected': { color: theme.palette.primary.main },
              }}
            />
          </Tabs>
        </Paper>
      </Box>

      {/* Contenido de los tabs - Dentro del Paper como en Categorías y Pruebas */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          width: '100%', 
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.05)}`
        }}
      >
        {tab === 0 && (
          <DashboardTab
            inscritos={inscritos}
            equiposCount={equipos.length}
            totalIngresos={totalIngresos}
            genero={genero}
            inscritosDiarios={inscritosDiarios}
            categoriaBars={categoriaBars}
          />
        )}

        {tab === 1 && <ListsTab />}
      </Paper>
    </Box>
  );
};

DashboardInscritos.propTypes = {
  competencia: PropTypes.object,
};

export default DashboardInscritos;